import AdobeAnalyticsService from "../services/adobe-analytics-service";
import AdobeAnalyticsPageType from "../enums/adobe-analytics-page-type";
import config from "../constants/config";

const { ENV_SUFIX } = config;

/**
 * Determine page type based on current URL path
 * @returns {string} - Adobe Analytics page type
 */
const getPageTypeFromUrl = () => {
  const path = window.location.pathname;
  
  // Signin identifier pages -> 'account login'
  if (
    path.includes('signin-identifier') ||
    path.includes('simplified-username') ||
    path.includes('simplified-cloud-username') ||
    path.includes('cloud-signin-identifier')
  ) {
    return AdobeAnalyticsPageType.MOBILE_LOGIN; // 'account login'
  }
  
  // Default page type
  return AdobeAnalyticsPageType.MOBILE_LOGIN;
};

/**
 * Get clientID and spName from OIDC context in gigyaCache sessionStorage
 * Same logic as getOIDCContext in signin-controllers.jsx
 * @returns {Object} - Object containing clientID and spName
 */
const getOIDCContextData = () => {
  try {
    const cookieValue = sessionStorage.getItem("gigyaCache");
    if (cookieValue && cookieValue !== "{}") {
      const cookieObj = JSON.parse(cookieValue);

      // Detect DCR flow
      const isDcr =
        window.location.href.includes("dcr") ||
        sessionStorage.getItem("gig_apikey") ===
          process.env[`REACT_APP_DCR_CDC_API_KEY${ENV_SUFIX}`];

      // Detect MFA flow
      const isMfa =
        window.location.href.includes("mfa") ||
        sessionStorage.getItem("gig_apikey") ===
          process.env[`REACT_APP_MFA_CDC_API_KEY${ENV_SUFIX}`];

      const defaultApiKey = process.env[`REACT_APP_CDC_API_KEY${ENV_SUFIX}`];
      let API_KEY = defaultApiKey;

      if (isDcr) {
        API_KEY =
          process.env[`REACT_APP_DCR_CDC_API_KEY${ENV_SUFIX}`] || defaultApiKey;
      } else if (isMfa) {
        API_KEY =
          process.env[`REACT_APP_MFA_CDC_API_KEY${ENV_SUFIX}`] || defaultApiKey;
      }

      const key = `gig_oidcContext_${API_KEY}`;
      let cookieData = cookieObj[key]?.response;

      // Fallback: try the gig_apikey stored by the proxy page
      if (!cookieData) {
        const storedApiKey = sessionStorage.getItem("gig_apikey");
        if (storedApiKey && storedApiKey !== API_KEY) {
          const fallbackKey = `gig_oidcContext_${storedApiKey}`;
          cookieData = cookieObj[fallbackKey]?.response;
        }
      }

      return {
        clientID: cookieData?.[0]?.clientID || '',
        spName: cookieData?.[0]?.state || ''
      };
    }
  } catch (error) {
    console.error("Failed to get OIDC context for analytics:", error);
  }
  return { clientID: '', spName: '' };
};

/**
 * Configure digital data user properties
 * Sets up user-specific analytics data on page load
 */
const configureDigitalDataUser = () => {
  if (!window.digitalData) return;

  // Delete form data if exists
  if (window.digitalData.form) {
    delete window.digitalData.form;
  }

  // Initialize user object if not exists
  if (!window.digitalData.user) {
    window.digitalData.user = {};
  }

  // Get OIDC context data (clientID and spName)
  const { clientID, spName } = getOIDCContextData();

  // Set user properties
  window.digitalData.user.jobFunction = '';
  window.digitalData.user.clientID = clientID; 
  window.digitalData.user.state = sessionStorage.getItem("referrer_state") || '';
  window.digitalData.user.spName = spName;

};

/**
 * Configure digital data on successful sign-in
 * Sets login status, gigya ID and dispatches userLogin event
 * @param {Object} response - Gigya login response containing UID
 */
export const configureDigitalDataOnSignIn = (response) => {
  if (!window.digitalData) return;

  // Delete form data if exists
  if (window.digitalData.form) {
    delete window.digitalData.form;
  }

  // Initialize user object if not exists
  if (!window.digitalData.user) {
    window.digitalData.user = {};
  }

  // Get OIDC context data (clientID and spName)
  const { clientID, spName } = getOIDCContextData();

  // Set user properties on sign-in
  window.digitalData.user.jobFunction = '';
  window.digitalData.user.loginStatus = 'logged in';
  window.digitalData.user.gigyaId = response?.UID || '';
  window.digitalData.user.clientID = clientID;
  window.digitalData.user.state = sessionStorage.getItem("referrer_state") || '';
  window.digitalData.user.spName = spName;

  // Dispatch custom userLogin event
  document.dispatchEvent(
    new CustomEvent('userLogin', { 
      detail: { user: { loginType: 'gigya' } } 
    })
  );
};

/**
 * Configure digital data on login error
 * Sets error fields, anonymous status and dispatches loginError event
 * @param {string} errorMsg - Error message to record
 */
export const configureDigitalDataOnError = (errorMsg) => {
  if (!window.digitalData) return;

  // Set form error fields
  window.digitalData.form = {
    errorFields: errorMsg
  };

  // Initialize user object if not exists
  if (!window.digitalData.user) {
    window.digitalData.user = {};
  }

  // Get OIDC context data (clientID and spName)
  const { clientID, spName } = getOIDCContextData();

  // Set user properties on error
  window.digitalData.user.jobFunction = '';
  window.digitalData.user.loginStatus = 'anonymous';
  window.digitalData.user.clientID = clientID;
  window.digitalData.user.state = sessionStorage.getItem("referrer_state") || '';
  window.digitalData.user.spName = spName;

  // Dispatch custom loginError event
  document.dispatchEvent(new CustomEvent('loginError', {}));
};

/**
 * Configure digital data for username required error
 * Sets error when username field is empty
 */
export const configureDigitalDataUsernameRequired = () => {
  configureDigitalDataOnError('Username is a required field');
};

/**
 * Configure digital data for password required error
 * Sets error when password field is empty
 */
export const configureDigitalDataPasswordRequired = () => {
  configureDigitalDataOnError('Password is a required field');
};

/**
 * Configure digital data for Adobe Analytics
 * Polls for window.digitalData to be available before configuring
 * @param {Object} options - Configuration options
 * @param {number} options.interval - Interval in ms between checks (default: 500)
 * @param {number} options.maxAttempts - Maximum number of attempts (default: 20)
 * @param {string} options.pageType - Adobe Analytics page type (default: auto-detect from URL)
 * @returns {Function} - Cleanup function to cancel polling
 */
export const configureDigitalData = (options = {}) => {
  const {
    interval = 500,
    maxAttempts = 20,
    pageType = getPageTypeFromUrl()
  } = options;

  let attempts = 0;
  let intervalId = null;

  // Check if Adobe Analytics is enabled
  const isAdobeEnabled = process.env[`REACT_APP_ADOBE_ENABLED${ENV_SUFIX}`] === "true";

  if (!isAdobeEnabled) {
    return () => {};
  }

  // Check immediately first
  if (window.digitalData) {
    AdobeAnalyticsService.configureDigitalData(pageType);
    configureDigitalDataUser();
    return;
  }

  // Poll for digitalData to be available
  intervalId = setInterval(() => {
    attempts++;

    if (window.digitalData) {
      AdobeAnalyticsService.configureDigitalData(pageType);
      configureDigitalDataUser();
      clearInterval(intervalId);
    } else if (attempts >= maxAttempts) {
      console.warn("digitalData not available after timeout");
      clearInterval(intervalId);
    }
  }, interval);
};

/**
 * Initialize Adobe Analytics with auto-detected page type
 */
export const initializeAdobeAnalytics = () => {
  configureDigitalData();
};
